import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import WebShell from './src/navigation/WebShell';
import Home from './src/screens/Home';
import Directory from './src/screens/Directory';
import MyDepartment from './src/screens/MyDepartment';
import Profile from './src/screens/Profile';
import Leave from './src/screens/Leave';
import Attendance from './src/screens/Attendance';
import Payslips from './src/screens/Payslips';
import Documents from './src/screens/Documents';
import Workflows from './src/screens/Workflows';
import Expenses from './src/screens/Expenses';
import Performance from './src/screens/Performance';
import Settings from './src/screens/Settings';
import { useAppStore } from './src/store/useAppStore';
import { checkSession, verifyMagicLink } from './src/services/auth';
import { handoffToPlatform, notInPlanUrl } from './src/lib/platformAuth';
import { PRODUCT } from './src/lib/planGate';
import VerifyEmail from './src/screens/VerifyEmail';
import AcceptInvitation from './src/screens/AcceptInvitation';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5, retry: 1 } },
});

function Spinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>Loading…</p>
    </div>
  );
}

// Handles /login, /signup routes — immediately hands off to platform.
function PlatformHandoff() {
  useEffect(() => { handoffToPlatform(`${window.location.origin}/`); }, []);
  return null;
}

function RequireAuth() {
  const { login, logout } = useAppStore();
  const [state, setState] = useState<'checking' | 'ok' | 'unavailable'>('checking');
  const [reason, setReason] = useState('');

  const probe = React.useCallback(() => {
    setState('checking');
    checkSession().then((result) => {
      if (result.status === 'authenticated') {
        const a = result.auth;
        login(a.token, a.role, a.profileId, a.firstName, a.displayName, a.email, a.orgId, a.orgName);
        setState('ok');
        return;
      }
      if (result.status === 'unauthenticated') {
        // The platform actually said so. This is the ONLY path that may end a session.
        logout();
        handoffToPlatform(window.location.href);
        return;
      }
      if (result.status === 'not_in_plan') {
        // Signed in, but this org's plan does not include the People website.
        // The session stays — their own console still needs it.
        window.location.assign(notInPlanUrl(PRODUCT));
        return;
      }
      // Could not get an answer. Signing the user out here is how a momentary
      // blip used to look like "logged out the instant I logged in" — so don't.
      setReason(result.reason);
      setState('unavailable');
    });
  }, []);

  useEffect(() => { probe(); }, [probe]);

  if (state === 'checking') return <Spinner />;
  if (state === 'unavailable') return <SessionUnavailable reason={reason} onRetry={probe} />;
  return <Outlet />;
}

function SessionUnavailable({ reason, onRetry }: { reason: string; onRetry: () => void }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
      <p style={{ color: 'var(--ink-1)', fontSize: 15, fontWeight: 600 }}>Can&rsquo;t reach the sign-in service</p>
      <p style={{ color: 'var(--ink-3)', fontSize: 13, maxWidth: 420 }}>
        You are still signed in. This is a connection problem, not a session problem.
      </p>
      <p style={{ color: 'var(--ink-3)', fontSize: 11, fontFamily: 'monospace' }}>{reason}</p>
      <button onClick={onRetry} style={{ marginTop: 4, padding: '8px 16px', fontSize: 13, borderRadius: 6, border: '1px solid var(--line-1)', background: 'var(--surface-1)', cursor: 'pointer' }}>
        Try again
      </button>
    </div>
  );
}

function MagicVerify() {
  const { login } = useAppStore();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'error'>('verifying');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) { handoffToPlatform(`${window.location.origin}/`); return; }
    verifyMagicLink(token)
      .then(({ token: jwt, role, profileId, firstName, displayName, email, orgId, orgName }) => {
        login(jwt, role, profileId, firstName, displayName, email, orgId, orgName);
        navigate('/', { replace: true });
      })
      .catch(() => {
        setStatus('error');
        setTimeout(() => handoffToPlatform(`${window.location.origin}/`), 2000);
      });
  }, []);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>
        {status === 'verifying' ? 'Signing you in…' : 'Link expired. Redirecting…'}
      </p>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login"             element={<PlatformHandoff />} />
          <Route path="/signup"            element={<PlatformHandoff />} />
          <Route path="/verify-email"      element={<VerifyEmail />} />
          <Route path="/auth/magic-verify" element={<MagicVerify />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />

          {/* Protected — WebShell is the layout, screens render via Outlet */}
          <Route element={<RequireAuth />}>
            <Route element={<WebShell />}>
              <Route index                        element={<Home />} />
              <Route path="/profile"              element={<Profile />} />
              <Route path="/profile/:employeeId"  element={<Profile />} />
              <Route path="/directory"            element={<Directory />} />
              <Route path="/my-department"        element={<MyDepartment />} />
              <Route path="/leave"                element={<Leave />} />
              <Route path="/attendance"           element={<Attendance />} />
              <Route path="/payroll"              element={<Payslips />} />
              <Route path="/documents"            element={<Documents />} />
              <Route path="/workflows"            element={<Workflows />} />
              <Route path="/expenses"             element={<Expenses />} />
              <Route path="/performance"          element={<Performance />} />
              <Route path="/settings"             element={<Settings />} />
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
